import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faPlus } from '@fortawesome/free-solid-svg-icons';
import DetailsEditor from './DetailsEditor';
import EnvironmentDetails from './EnvironmentDetails';
import EnvironmentSummary from './EnvironmentSummary';
import EnvironmentChat from './EnvironmentChat';
import ChatButton from './ChatButton';
import { useChatContext } from '../contexts/ChatContext';
import { 
  useEnvironments,
  useEnvironment, 
  useCreateEnvironment, 
  useUpdateEnvironment, 
  useDeleteEnvironment,
  CreateEnvironmentRequest,
  UpdateEnvironmentRequest,
  Environment
} from '../hooks/useEnvironments';
import styles from './GitHubConfiguration.module.scss';
import '../styles/shared.scss';

const EnvironmentsPage: React.FC = () => {
  const { tenantId, environmentId } = useParams<{ tenantId: string; environmentId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const isResizingRef = useRef(false);
  
  const { getUnreadCount } = useChatContext();
  const isEditMode = location.pathname.includes('/edit');
  const isChatMode = location.pathname.includes('/chat');
  const isSummaryMode = location.pathname.includes('/summary') || (!isEditMode && !isChatMode && environmentId);

  const { data: environments = [], isLoading, error } = useEnvironments(tenantId || '');
  const { data: environment } = useEnvironment(
    tenantId || '', 
    environmentId || ''
  );
  
  const createEnvironmentMutation = useCreateEnvironment();
  const updateEnvironmentMutation = useUpdateEnvironment();
  const deleteEnvironmentMutation = useDeleteEnvironment();

  const handleCreateEnvironment = () => {
    navigate(`/tenants/${tenantId}/environments`);
    setShowCreateForm(true);
  };

  const handleEnvironmentSelect = (env: Environment) => {
    navigate(`/tenants/${tenantId}/environments/${env.id}`);
    setShowCreateForm(false);
  };

  const handleFormComplete = () => {
    setShowCreateForm(false);
    navigate(`/tenants/${tenantId}/environments`);
  };

  const handleSave = async (data: CreateEnvironmentRequest | UpdateEnvironmentRequest) => {
    if (!tenantId) return;
    
    if (environmentId) {
      await updateEnvironmentMutation.mutateAsync({
        tenantId,
        environmentId,
        data: data as UpdateEnvironmentRequest
      });
    } else {
      await createEnvironmentMutation.mutateAsync({
        tenantId,
        data: data as CreateEnvironmentRequest
      });
    }
    handleFormComplete();
  };

  const handleDelete = async () => {
    if (!tenantId || !environmentId) return;
    
    await deleteEnvironmentMutation.mutateAsync({
      tenantId,
      environmentId
    });
    handleFormComplete();
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;
    const newWidth = Math.max(250, Math.min(600, e.clientX));
    setSidebarWidth(newWidth);
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false;
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    isResizingRef.current = true;
    e.preventDefault();
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const selectedEnvironment = environmentId ? environments.find(env => env.id === environmentId) || null : null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!tenantId) {
    return <div>Tenant ID is required</div>;
  }

  return (
    <div className={styles.container}>
      {/* Left Column - Environments List */}
      <div 
        className={styles.sidebar} 
        style={{ width: `${sidebarWidth}px` }}
      >
        <div className={styles.header}>
          <div className="page-title-group">
            <h3>
              <FontAwesomeIcon icon={faServer} style={{ marginRight: '8px' }} />
              Environments
            </h3>
          </div>
          <div>
            <button
              onClick={handleCreateEnvironment}
              className="create-button"
              title="Create Environment"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>
        
        <div>
          {isLoading ? (
            <p className={styles.statusText}>
              Loading environments...
            </p>
          ) : error ? (
            <p className={styles.errorText}>
              Error loading environments
            </p>
          ) : environments.length === 0 ? (
            <p className={styles.statusText}>
              No environments configured
            </p>
          ) : (
            environments.map((env) => (
              <div
                key={env.id}
                onClick={() => handleEnvironmentSelect(env)}
                className={`${styles.connectionItem} ${selectedEnvironment?.id === env.id ? styles.selected : ''}`}
              >
                <div className={styles.connectionHeader}>
                  <FontAwesomeIcon 
                    icon={faServer} 
                    className={styles.accessMethodIcon}
                  />
                  <div className={styles.connectionName}>
                    {env.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div onClick={(e) => e.stopPropagation()}>
                      <ChatButton
                        onClick={() => navigate(`/tenants/${tenantId}/environments/${env.id}/chat`)}
                        unreadCount={getUnreadCount(env.id)}
                        size="small"
                        tooltip={`Chat about ${env.name}`}
                      />
                    </div>
                    <div className={`${styles.status} ${env.status === 'active' ? styles.active : styles.inactive}`}>
                      {env.status === 'active' ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
                <div className={styles.connectionMeta}>
                  <div>
                    <span className={styles.typeTag}>{env.type.toUpperCase()}</span>
                    {env.description || 'No description'}
                  </div>
                  <div>{formatDate(env.createdAt)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Resize Handle */}
      <div 
        className={styles.resizeHandle}
        onMouseDown={handleMouseDown}
      />

      {/* Right Column - Details/Form */}
      <div className={styles.content}>
        {showCreateForm ? (
          <DetailsEditor
            title="Create Environment"
            showDelete={false}
            onComplete={handleFormComplete}
          >
            <EnvironmentDetails 
              environment={null}
              loading={false}
              onSave={handleSave}
              isCreateMode={true}
            />
          </DetailsEditor>
        ) : selectedEnvironment && isEditMode ? (
          <DetailsEditor
            title="Edit Environment"
            onComplete={handleFormComplete}
          >
            <EnvironmentDetails 
              environment={environment || null}
              loading={false}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          </DetailsEditor>
        ) : selectedEnvironment && isChatMode ? (
          <EnvironmentChat />
        ) : selectedEnvironment && isSummaryMode ? (
          <EnvironmentSummary />
        ) : (
          <div className={styles.placeholder}>
            {isEditMode ? 'Select an environment to edit' : 
             isChatMode ? 'Select an environment to chat about' :
             'Select an environment or create a new one'}
          </div>
        )}
      </div>
    </div>
  );
};

export default EnvironmentsPage;